/**
 * User module service — SSOT for every HTTP call in the user domain.
 *
 * Wraps the 5 endpoints that used to be invoked ad-hoc from the
 * ProfileView + useProfileForm call sites:
 *   - POST  /api/user/update-name/
 *   - PATCH /api/profile/
 *   - GET   /api/user/preferences/
 *   - PATCH /api/user/preferences/
 *   - GET   /api/backgrounds/
 *
 * Consumers call the typed functions below. `apiClient` never leaks
 * out of this file (enforced by ESLint `no-restricted-imports`).
 */

import { apiClient } from '../../apiClient';

// ── DTOs ────────────────────────────────────────────────────────────

export type UpdateNameRequest = {
    first_name: string;
    last_name: string;
};

export type ProfilePatchRequest = {
    background?: string;
    focus_areas?: string;
    desire?: string;
    anxieties?: string;
    industry_knowledge?: number | null;
    avatar_url?: string | null;
    location?: string;
    experience?: string;
};

export type PreferencesDTO = {
    email_notifications: boolean;
    show_online_status: boolean;
    dark_mode: boolean;
    language: string;
    timezone: string;
};

export type IdentityDTO = {
    /** 'google' when a Google SocialAccount is linked; 'email' otherwise. */
    provider: 'google' | 'email';
    /** Email surfaced on the sign-in card — prefers the Google extra_data
     *  email when a social account is linked, else the primary user email. */
    email: string;
    last_login_at: string | null;
};

export type NotificationPreference = {
    /** Dotted scope — either a top-level category (e.g. "networking") or a
     *  full type (e.g. "networking.connection_received"). */
    scope: string;
    muted: boolean;
    email_digest: 'off' | 'daily' | 'weekly';
};

export type DataExportResponse = {
    /** Full GDPR bundle. Shape is intentionally open — consumers save it
     *  as JSON blob for download rather than rendering fields. */
    [k: string]: unknown;
};

export type SessionDTO = {
    id: number;
    /** Truncated JTI (first 8 chars) for device identification in UI. */
    jti_prefix: string;
    device_ua: string;
    ip: string | null;
    created_at: string | null;
    last_seen_at: string | null;
    revoked_at: string | null;
    /** True when this session matches the current request's JWT. */
    is_current: boolean;
};

// ── Calls ───────────────────────────────────────────────────────────

export async function updateUserName(payload: UpdateNameRequest): Promise<void> {
    await apiClient.post('/api/user/update-name/', payload);
}

export async function patchProfile(payload: ProfilePatchRequest): Promise<void> {
    await apiClient.patch('/api/profile/', payload);
}

export async function getPreferences(): Promise<Partial<PreferencesDTO>> {
    const res = await apiClient.get<Partial<PreferencesDTO>>('/api/user/preferences/');
    return res.data;
}

export async function patchPreferences(patch: Partial<PreferencesDTO>): Promise<void> {
    await apiClient.patch('/api/user/preferences/', patch);
}

export async function listBackgrounds(): Promise<string[]> {
    const res = await apiClient.get<string[]>('/api/backgrounds/');
    return res.data ?? [];
}

/** Read the current user's raw profile (name + email fields) — used by
 *  places that only need the inline header identity, not the full
 *  BackendUserWithPlan shape that `userService.fetchCurrentUserWithPlan`
 *  returns. */
export async function getProfile(): Promise<{
    first_name?: string;
    last_name?: string;
    email?: string;
    [k: string]: unknown;
}> {
    const res = await apiClient.get('/api/profile/');
    return res.data ?? {};
}

// ── Phase 2: identity + notifications + privacy + deactivate ────────

/** Sign-in method + last login for Settings › Security.
 *  Backed by GET /api/user/identity/ (see Django view get_identity). */
export async function getIdentity(): Promise<IdentityDTO | null> {
    try {
        const res = await apiClient.get<IdentityDTO>('/api/user/identity/');
        return res.data ?? null;
    } catch (err) {
        console.warn('[userService] Failed to fetch identity', err);
        return null;
    }
}

/** List the current user's notification preferences.
 *  Missing entries mean "use the default for that category." */
export async function listNotificationPreferences(): Promise<NotificationPreference[]> {
    const res = await apiClient.get<{ results?: NotificationPreference[] } | NotificationPreference[]>(
        '/api/notifications/preferences/',
    );
    const body = res.data;
    if (Array.isArray(body)) return body;
    return body?.results ?? [];
}

/** Upsert a single notification preference row (category or full type). */
export async function upsertNotificationPreference(
    scope: string,
    patch: Partial<Pick<NotificationPreference, 'muted' | 'email_digest'>>,
): Promise<NotificationPreference> {
    const res = await apiClient.put<NotificationPreference>(
        `/api/notifications/preferences/${encodeURIComponent(scope)}/`,
        patch,
    );
    return res.data;
}

/** Delete a preference row (reverts that scope to the default). */
export async function deleteNotificationPreference(scope: string): Promise<void> {
    await apiClient.delete(`/api/notifications/preferences/${encodeURIComponent(scope)}/`);
}

/** GDPR data export — returns a JSON bundle the caller can download. */
export async function exportData(): Promise<DataExportResponse> {
    const res = await apiClient.get<DataExportResponse>('/api/user/export/');
    return res.data ?? {};
}

/** Soft-deactivate the current account (30-day grace). After success
 *  the next authenticated request will 401 and the SPA should sign
 *  the user out. */
export async function deactivateAccount(): Promise<void> {
    await apiClient.post('/api/user/delete/', {});
}

// ── Phase 5: active sessions ─────────────────────────────────────

/** List the current user's device sessions, newest first. */
export async function listSessions(): Promise<SessionDTO[]> {
    const res = await apiClient.get<{ results: SessionDTO[] }>('/api/user/sessions/');
    return res.data?.results ?? [];
}

/** Revoke one device session. Server best-effort blacklists the jti
 *  so any refresh attempt with that token 401s. */
export async function revokeSession(id: number): Promise<SessionDTO> {
    const res = await apiClient.post<SessionDTO>(`/api/user/sessions/${id}/revoke/`, {});
    return res.data;
}
